import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ServiceProviderCard from '../components/ServiceProviderCard';
import { collection, getDocs, query } from 'firebase/firestore';
import { db } from '../config/firebase';
import './ServiceProviderList.css';
import { useQuery } from 'react-query';
import { serviceProvidersData } from '../data/serviceProviders';
import Footer from '../components/Footer';
import { useTracking } from '../hooks/useTracking';
import { trackEvent } from '../utils/analytics';

const ServiceProviderList = ({ isAdmin, onDelete }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [expandedCategory, setExpandedCategory] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { trackButtonClick } = useTracking();

  const { data: providers = [], isLoading, error } = useQuery(
    'providers',
    async () => {
      try {
        // Fetch providers from Firebase
        const snapshot = await getDocs(collection(db, 'providers'));
        const firebaseData = await Promise.all(snapshot.docs.map(async doc => {
          const providerId = doc.id;
          const providerData = doc.data();
          
          // Fetch reviews for this provider
          const reviewsRef = collection(db, 'providers', providerId, 'reviews');
          const reviewsSnapshot = await getDocs(reviewsRef);
          const reviews = reviewsSnapshot.docs.map(review => review.data());
          
          // Calculate average rating
          const totalRating = reviews.reduce((sum, review) => sum + review.rating, 0);
          const averageRating = reviews.length > 0 
            ? totalRating / reviews.length  // Don't round here to keep decimal precision
            : 0;

          console.log(`Provider ${providerData.name} has ${reviews.length} reviews with average rating ${averageRating}`);

          return {
            id: providerId,
            ...providerData,
            averageRating,
            numberOfReviews: reviews.length
          };
        }));
        
        // Only use local data if Firebase fetch returns empty
        if (firebaseData.length === 0) {
          console.log('No Firebase data found, using local data');
          return serviceProvidersData;
        }
        
        return firebaseData.sort((a, b) => a.category.localeCompare(b.category));
      } catch (error) {
        console.error('Firebase fetch failed:', error);
        return serviceProvidersData;
      }
    },
    {
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false
    }
  );

  // Group providers by category
  const groupedProviders = providers.reduce((acc, provider) => {
    const category = provider.category || 'Uncategorized';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(provider);
    
    // Sort providers within each category by priority
    acc[category].sort((a, b) => {
      if (a.isPriorityListing && !b.isPriorityListing) return -1;
      if (!a.isPriorityListing && b.isPriorityListing) return 1;
      return a.name.localeCompare(b.name);
    });
    
    return acc;
  }, {});

  // Filter providers based on search term
  const filterProviders = (providers) => {
    return providers.filter(provider => {
      const matchesSearch = !searchTerm || 
        provider.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        provider.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        provider.phoneNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        provider.email?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCategory = selectedCategory === 'all' || 
        provider.category.toLowerCase() === selectedCategory.toLowerCase();

      const matchesLocation = selectedLocation === 'all' ||
        provider.location?.toLowerCase() === selectedLocation.toLowerCase();

      return matchesSearch && matchesCategory && matchesLocation;
    });
  };

  // Get unique locations from providers
  const locations = [...new Set(providers
    .map(provider => provider.location?.trim())
    .filter(Boolean)
  )].sort();

  // Update useEffect to handle category changes from navigation
  useEffect(() => {
    if (location.state?.selectedCategory) {
      setSelectedCategory(location.state.selectedCategory.toLowerCase());
    }
  }, [location.state]);

  // Add this useEffect for auto-scroll on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []); // Empty dependency array ensures it runs only on mount

  useEffect(() => {
    // Track page view
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: 'Service Providers',
        page_location: window.location.href,
        page_path: '/providers'
      });
    }
    
    // Also track using our custom tracking utility
    trackEvent('page_view', {
      page_title: 'Service Providers',
      page_location: window.location.href,
      page_path: '/providers'
    });
  }, []); // Empty dependency array means this runs once when component mounts

  const handleCategoryClick = (category) => {
    trackButtonClick('category_filter', { category });
    setSelectedCategory(category.toLowerCase());
  };

  const handleLocationChange = (e) => {
    const location = e.target.value;
    trackButtonClick('location_filter', { location });
    setSelectedLocation(location);
  };

  if (isLoading) return (
    <div className="providers-loading">
      <div className="loading-spinner"></div>
    </div>
  );
  if (error) return <div className="error">{error.message}</div>;

  return (
    <>
      <div className="provider-list">
        <h1>Service Providers Directory</h1>
        <div className="filters-container">
          <input
            type="text"
            className="search-bar"
            placeholder="Search by name, category, phone, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <select 
            value={selectedCategory}
            onChange={(e) => handleCategoryClick(e.target.value)}
            className="category-filter"
          >
            <option value="all">All Categories</option>
            {Object.keys(groupedProviders).sort().map(category => (
              <option key={category} value={category.toLowerCase()}>
                {category}
              </option>
            ))}
          </select>
          <select
            value={selectedLocation}
            onChange={(e) => handleLocationChange(e)}
            className="category-filter"
          >
            <option value="all">All Locations</option>
            {locations.map(location => (
              <option key={location} value={location.toLowerCase()}>
                {location}
              </option>
            ))}
          </select>
        </div>
        
        <div className="categories-container">
          {Object.entries(groupedProviders)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([category, categoryProviders]) => {
              const filteredProviders = filterProviders(categoryProviders);
              if (filteredProviders.length === 0) return null;
              
              const isExpanded = expandedCategory === category;
              
              return (
                <div key={category} className="category-section">
                  <h2 
                    className={`category-title ${isExpanded ? 'expanded' : ''}`}
                    onClick={() => {
                      // Only toggle on mobile
                      if (window.innerWidth <= 768) {
                        setExpandedCategory(isExpanded ? null : category);
                      }
                    }}
                  >
                    {category}
                    {window.innerWidth <= 768 && (
                      <span className="expand-icon">
                        {isExpanded ? '−' : '+'}
                      </span>
                    )}
                  </h2>
                  <div className={`providers-grid ${isExpanded ? 'expanded' : ''}`}>
                    {filteredProviders.map(provider => (
                      <ServiceProviderCard 
                        key={provider.id} 
                        provider={provider}
                        isAdmin={isAdmin}
                        onDelete={onDelete}
                      />
                    ))}
                  </div>
                </div>
              );
          })}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ServiceProviderList;
